import { c as _c } from "react/compiler-runtime";
import { TextLink as TextLinkComponent, headlessLinkProps } from 'design-system/server';
import Link, { type LinkProps } from 'next/link';
import { ComponentProps, type ForwardedRef, forwardRef } from 'react';
import { urlToString } from '@/helpers/utils/routing/Routes';
import { Link as NextIntlLink } from '@/i18n/navigation';
type TextLinkProps = Omit<ComponentProps<typeof TextLinkComponent>, 'href'> & Pick<ComponentProps<typeof Link>, 'href'>;
type LocaleAwareTextLinkProps = Omit<ComponentProps<typeof TextLinkComponent>, 'href'> & Pick<ComponentProps<typeof NextIntlLink>, 'href' | 'locale' | 'lang'>;
const TextLink = forwardRef(function TextLink(t0, ref) {
  const $ = _c(15);
  let href;
  let prefetch;
  let props;
  let rel;
  let t1;
  if ($[0] !== t0) {
    ({
      href,
      external: t1,
      prefetch,
      rel,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = href;
    $[2] = prefetch;
    $[3] = props;
    $[4] = rel;
    $[5] = t1;
  } else {
    href = $[1];
    prefetch = $[2];
    props = $[3];
    rel = $[4];
    t1 = $[5];
  }
  const external = t1 === undefined ? false : t1;
  if (external || rel) {
    let t2;
    if ($[6] !== href || $[7] !== props || $[8] !== ref) {
      t2 = <ExternalLink ref={ref} href={href} {...props} />;
      $[6] = href;
      $[7] = props;
      $[8] = ref;
      $[9] = t2;
    } else {
      t2 = $[9];
    }
    return t2;
  }
  let t2;
  if ($[10] !== href || $[11] !== prefetch || $[12] !== props || $[13] !== ref) {
    t2 = <InternalLink prefetch={prefetch} ref={ref} href={href} {...props} />;
    $[10] = href;
    $[11] = prefetch;
    $[12] = props;
    $[13] = ref;
    $[14] = t2;
  } else {
    t2 = $[14];
  }
  return t2;
});
const LocaleAwareLink = forwardRef(function LocaleAwareLink(t0, ref) {
  const $ = _c(17);
  let href;
  let lang;
  let locale;
  let prefetch;
  let t1;
  if ($[0] !== t0) {
    const {
      href: t2,
      locale: t3,
      lang: t4,
      prefetch: t5,
      ...props
    } = t0;
    href = t2;
    locale = t3;
    lang = t4;
    prefetch = t5;
    t1 = headlessLinkProps(props);
    $[0] = t0;
    $[1] = href;
    $[2] = lang;
    $[3] = locale;
    $[4] = prefetch;
    $[5] = t1;
  } else {
    href = $[1];
    lang = $[2];
    locale = $[3];
    prefetch = $[4];
    t1 = $[5];
  }
  let children;
  let props_;
  if ($[6] !== t1) {
    const {
      children: t2,
      as: _,
      ...t3
    } = t1;
    children = t2;
    props_ = t3;
    $[6] = t1;
    $[7] = children;
    $[8] = props_;
  } else {
    children = $[7];
    props_ = $[8];
  }
  let t2;
  if ($[9] !== children || $[10] !== href || $[11] !== lang || $[12] !== locale || $[13] !== prefetch || $[14] !== props_ || $[15] !== ref) {
    t2 = <NextIntlLink prefetch={prefetch} ref={ref} href={href} locale={locale} lang={lang} {...props_}>{children}</NextIntlLink>;
    $[9] = children;
    $[10] = href;
    $[11] = lang;
    $[12] = locale;
    $[13] = prefetch;
    $[14] = props_;
    $[15] = ref;
    $[16] = t2;
  } else {
    t2 = $[16];
  }
  return t2;
});
const InternalLink = forwardRef(function InternalTextLink(t0, ref) {
  const $ = _c(11);
  let href;
  let t1;
  if ($[0] !== t0) {
    const {
      href: t2,
      ...props
    } = t0;
    href = t2;
    t1 = headlessLinkProps(props);
    $[0] = t0;
    $[1] = href;
    $[2] = t1;
  } else {
    href = $[1];
    t1 = $[2];
  }
  let children;
  let props_;
  if ($[3] !== t1) {
    const {
      children: t2,
      as: _,
      ...t3
    } = t1;
    children = t2;
    props_ = t3;
    $[3] = t1;
    $[4] = children;
    $[5] = props_;
  } else {
    children = $[4];
    props_ = $[5];
  }
  let t2;
  if ($[6] !== children || $[7] !== href || $[8] !== props_ || $[9] !== ref) {
    t2 = <Link ref={ref} href={href} {...props_}>{children}</Link>;
    $[6] = children;
    $[7] = href;
    $[8] = props_;
    $[9] = ref;
    $[10] = t2;
  } else {
    t2 = $[10];
  }
  return t2;
});
const ExternalLink = forwardRef(function ExternalTextLink(t0, ref) {
  const $ = _c(9);
  let href;
  let props;
  if ($[0] !== t0) {
    const {
      href: t1,
      as: _,
      ...t2
    } = t0;
    href = t1;
    props = t2;
    $[0] = t0;
    $[1] = href;
    $[2] = props;
  } else {
    href = $[1];
    props = $[2];
  }
  let t1;
  if ($[3] !== href) {
    t1 = urlToString(href);
    $[3] = href;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  const href_ = t1;
  let t2;
  if ($[5] !== href_ || $[6] !== props || $[7] !== ref) {
    t2 = <TextLinkComponent ref={ref} href={href_} {...props} />;
    $[5] = href_;
    $[6] = props;
    $[7] = ref;
    $[8] = t2;
  } else {
    t2 = $[8];
  }
  return t2;
});
export default TextLink;
export { LocaleAwareLink };